import { Button, Popover, PopoverContent } from '@orobix/orobix-fe-components'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { update } from '../../../utils/api'
import { DrugStatusIcon } from './DrugStatusIcon'

type DrugStatusEditorProps = {
  minsan: string
  machinable: boolean
  manualOverride: boolean
  manualOverrideBy: string | null
  manualOverrideAt: Date | null
}

// A one-click alternative to the bulk-upload CSV for the single-drug case:
// clicking the status icon opens a small popover to flip `machinable`
// directly, calling the same manual-override endpoint bulk-upload uses
// under the hood (PATCH /drug-registry/set-machinable/).
export const DrugStatusEditor = ({
  minsan,
  machinable,
  manualOverride,
  manualOverrideBy,
  manualOverrideAt,
}: DrugStatusEditorProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { mutate } = useMutation(
    ['drug-registry', 'set-machinable', minsan],
    // `null` is the third state the endpoint takes: it drops the override and
    // lets the automatic rule decide again.
    (value: boolean | null) =>
      update('/drug-registry/set-machinable/', {
        minsan,
        machinable: value,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['drug-registry'])
      },
    }
  )

  return (
    <Popover
      renderContent={({ setClose }) => (
        <PopoverContent width="10rem" clearStyles noMarginBottom>
          <Button
            label={t('Machinable')}
            type="blank-default"
            onClick={() => {
              mutate(true)
              setClose()
            }}
          />
          <Button
            label={t('Not machinable')}
            type="blank-default"
            onClick={() => {
              mutate(false)
              setClose()
            }}
          />
          {/* Only for a row someone has already set by hand: on an automatic
              row there is nothing to hand back. Both buttons above write an
              override, so without this one a wrong click could not be undone
              short of a database update — and re-picking the old value would
              not do it, since the row would stay outside the rule. */}
          {manualOverride && (
            <Button
              label={t('Automatic')}
              title={t(
                'Removes the manual decision: the state goes back to being computed by the system'
              )}
              type="blank-default"
              onClick={() => {
                mutate(null)
                setClose()
              }}
            />
          )}
        </PopoverContent>
      )}
    >
      <span style={{ cursor: 'pointer' }}>
        <DrugStatusIcon
          machinable={machinable}
          manualOverride={manualOverride}
          manualOverrideBy={manualOverrideBy}
          manualOverrideAt={manualOverrideAt}
        />
      </span>
    </Popover>
  )
}
